import { Tooltip, Upload, message } from 'antd';
import { ToolbarIcon } from '../../ToolbarIcon';
import { useTranslate } from '@tolgee/react';
import { platformUploadImageAcceptMap, platformUploadSizeLimitMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { useMemoizedFn } from 'ahooks';
import { RcFile } from 'antd/lib/upload';
import {
  MsgAttachmentInfo,
  useMsgAttachmentList,
  useUploadAttachment,
} from './hooks/useUploadAttachment';
import { uploadClass } from '../styles';

interface AttachmentUploadActionProps {
  conversation: Conversation.Conversation;
}

const UPLOAD_MAX_COUNT = 3;

const getFileExtension = (name: string) => {
  const lastDot = name.lastIndexOf('.');
  return lastDot === -1 ? '' : name.slice(lastDot + 1).toLowerCase();
};

export const AttachmentUploadActionComponent = (props: {
  channel: General.PlatformUpperCaseTunnel;
  attachmentList: Util.PartialPartial<MsgAttachmentInfo, 'storeId'>[];
  onAttachmentUpload: (file: RcFile) => any;
  children?: JSX.Element;
}) => {
  const { channel, attachmentList, onAttachmentUpload, children } = props;

  const { t } = useTranslate();
  const isJolect = channel === 'JOLECT';
  const maxCount = isJolect ? 1 : UPLOAD_MAX_COUNT;

  const handleBeforeUpload = useMemoizedFn((file: RcFile, fileList: RcFile[]) => {
    const channelKey = toLowerCase(channel);
    const uploadSizeLimit = platformUploadSizeLimitMap[channelKey];
    if (fileList.length > maxCount || attachmentList.length >= maxCount) {
      message.warn(
        t('scene.chatWorkspace.messageEditor.upload.validation.maxAttachmentCount', {
          count: maxCount,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    if (file.size > uploadSizeLimit) {
      message.warn(
        t('upload.limitWarn', {
          size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}M`,
        }),
      );
      return Upload.LIST_IGNORE;
    }
    if (isJolect) {
      const allowedExtensions = platformUploadImageAcceptMap[channelKey];
      if (!allowedExtensions.includes(getFileExtension(file.name))) {
        message.warn(
          t('upload.fileLimit', {
            size: `${(uploadSizeLimit / 1024 / 1024).toFixed(2)}`,
            type: allowedExtensions.join(', '),
          }),
        );
        return Upload.LIST_IGNORE;
      }
    }

    onAttachmentUpload(file);

    return Upload.LIST_IGNORE;
  });
  return (
    <Tooltip title={t('scene.chatWorkspace.messageEditor.upload.action.uploadFiles')}>
      <Upload
        className={uploadClass}
        name="file"
        accept={platformUploadImageAcceptMap[toLowerCase(channel)]
          .map((type) => `.${type}`)
          .join(',')}
        multiple={!isJolect}
        maxCount={maxCount}
        showUploadList={false}
        beforeUpload={handleBeforeUpload}
      >
        {children || <ToolbarIcon icon="attachment" />}
      </Upload>
    </Tooltip>
  );
};

export const AttachmentUploadAction = (props: AttachmentUploadActionProps) => {
  const {
    conversation: { conversationId, channel, storeId, region },
  } = props;
  const uploadAttachment = useUploadAttachment();
  const msgAttachmentList = useMsgAttachmentList(conversationId);

  return (
    <AttachmentUploadActionComponent
      channel={channel}
      attachmentList={msgAttachmentList}
      onAttachmentUpload={(file) =>
        uploadAttachment(conversationId, storeId, region, file, {
          withMediaMetadata: channel === 'JOLECT',
        })
      }
    />
  );
};
